import { EditorProps } from 'prosemirror-view'
import { ReactPortal } from 'react'

import {
  AISuggestion,
  Box,
  Card,
  Code,
  ColumnGroup,
  GithubEmbed,
  Iframe,
  Image,
  JiraEmbed,
  LineDivider,
  ListItem,
  MentionSmartLink,
  OrderedList,
  Table,
  TableOfContents,
  ToggleList,
  VoteListItem,
} from 'common/Prose/nodeViews'
import { createIframeNodeView } from 'common/Prose/nodeViews/Iframe/IframeNodeView'
import { createReactNodeView } from 'common/Prose/nodeViews/ReactNodeView/ReactNodeView'
import { createTableNodeView } from 'common/Prose/nodeViews/Table/TableNodeView'
import { ReadTodoListItem, TodoListItem } from 'common/Prose/nodeViews/TodoListItem/TodoListItem'
import { reactNodeViewFactory } from 'common/Prose/nodeViews/utils/reactNodeViewFactory'
import { Download } from 'proseShared/nodeViews/Download/Download'
import { MermaidComponent } from 'proseShared/nodeViews/Mermaid/components/MermaidComponent/MermaidComponent'
import { ImagePreview } from 'common/Prose/nodeViews/Image/Image'

interface Config {
  readOnly?: boolean
  inCommentMode?: boolean
  createPortal: (portal: ReactPortal) => void
  removePortal: (key: string) => void
}

export const nerudaNodeViews = ({ readOnly = false, inCommentMode = false, createPortal, removePortal }: Config) => {
  const makeReactNodeViewFn = reactNodeViewFactory(createPortal, removePortal, inCommentMode)
  const nodeViews: EditorProps['nodeViews'] = {
    ai_suggestion: makeReactNodeViewFn(AISuggestion),
    box: (node, view, getPos, _) => new Box(node, view, getPos),
    table_of_contents: makeReactNodeViewFn(TableOfContents),
    download: (node, view, getPos, decorations) => new Download(node, view, getPos, decorations, readOnly),
    image: makeReactNodeViewFn(readOnly ? ImagePreview : Image),
    ordered_list: (node, view, getPos, _) => new OrderedList(node, view, getPos),
    list_item: (node, view, getPos, decorations) => {
      const $pos = view.state.doc.resolve(getPos())
      if ($pos.parent.type === view.state.schema.nodes.check_list) {
        return createReactNodeView({
          node,
          view,
          getPos,
          decorations,
          component: readOnly || inCommentMode ? ReadTodoListItem : TodoListItem,
          onCreatePortal: createPortal,
          onDestroyPortal: removePortal,
          inCommentMode: inCommentMode,
          containerElementType: 'li',
        })
      }

      return new ListItem(node, view, getPos)
    },

    card: makeReactNodeViewFn(Card),
    smartLink: makeReactNodeViewFn(MentionSmartLink),
    mermaid_embed: makeReactNodeViewFn(MermaidComponent),
    github_embed: makeReactNodeViewFn(GithubEmbed),
    vote_list_item: (node, view, getPos, decorations) =>
      createReactNodeView({
        node,
        view,
        getPos,
        decorations,
        component: VoteListItem,
        onCreatePortal: createPortal,
        onDestroyPortal: removePortal,
        containerElementType: 'li',
      }),
    line_divider: makeReactNodeViewFn(LineDivider),

    jira_embed: makeReactNodeViewFn(JiraEmbed),

    table: (node, view, getPos, decorations) =>
      createTableNodeView({
        node,
        view,
        getPos,
        decorations,
        component: Table,
        onCreatePortal: createPortal,
        onDestroyPortal: removePortal,
      }),
    toggle_list: makeReactNodeViewFn(ToggleList),
    code_block: (node, view, getPos, decorations) => new Code(node, view, getPos, decorations, readOnly),
    column_group: makeReactNodeViewFn(ColumnGroup),
    iframe: (node, view, getPos, decorations) =>
      createIframeNodeView({
        node,
        view,
        getPos,
        decorations,
        component: Iframe,
        onCreatePortal: createPortal,
        onDestroyPortal: removePortal,
      }),
  }

  return nodeViews
}
